import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io, type Socket } from 'socket.io-client';

import { authActions, SESSION_ID_KEY } from '@/redux/auth/authSlice';
import type { RootState } from '@/redux/store';
import { clearAuthStorage, redirectToSignedOut } from '@/services/auth';
import { SCULPX_USER_SWITCH_KEY } from '@/services/accountsUserSwitch';

function getAccountsSocketOrigin(): string {
  const raw = String(import.meta.env.VITE_AUTH_API_BASE_URL || '').replace(/\/+$/, '');
  if (!raw) return 'http://localhost:5000';
  try {
    const u = new URL(raw.includes('/api/') ? raw : `${raw}/api/v1`);
    return u.origin;
  } catch {
    return 'http://localhost:5000';
  }
}

type SessionRevokedPayload = {
  sessionId: string;
  reason?: 'max_sessions_exceeded' | 'logout' | string;
};

export function useAccountsSessionSync() {
  const dispatch = useDispatch();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const socketRef = useRef<Socket | null>(null);
  const logoutIssuedRef = useRef(false);

  const forceLogout = useCallback(() => {
    if (logoutIssuedRef.current) return;
    logoutIssuedRef.current = true;
    clearAuthStorage();
    dispatch(authActions.clearAuth());
    redirectToSignedOut();
  }, [dispatch]);

  useEffect(() => {
    if (!accessToken) {
      logoutIssuedRef.current = false;
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    const socket = io(getAccountsSocketOrigin(), {
      auth: { token: accessToken },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 5,
    });

    socket.on('session:revoked', ({ sessionId, reason }: SessionRevokedPayload) => {
      const stored = localStorage.getItem(SESSION_ID_KEY);
      if (stored && stored !== sessionId) return;
      if (reason === 'max_sessions_exceeded') {
        window.setTimeout(forceLogout, 800);
      } else {
        forceLogout();
      }
    });

    socketRef.current = socket;
    return () => {
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [accessToken, forceLogout]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== SCULPX_USER_SWITCH_KEY) return;
      clearAuthStorage();
      dispatch(authActions.clearAuth());
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [dispatch]);
}
